export default function TermsOfServicePage() {
  const lastUpdated = "November 11, 2025";

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="container mx-auto max-w-4xl">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 md:p-12">
          {/* Header */}
          <div className="mb-8 pb-6 border-b border-gray-200">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Terms of Service
            </h1>
            <p className="text-gray-600">
              Last Updated: <span className="font-medium">{lastUpdated}</span>
            </p>
          </div>

          {/* Content */}
          <div className="prose prose-gray max-w-none space-y-8">
            {/* Introduction */}
            <section>
              <p className="text-gray-700 leading-relaxed">
                These Terms of Service ("Terms") apply to your access and use of euronymo ·
                PermitBuddy (the "Service"), a web application that helps electrical contractors
                find permit requirements and calculate permit fees. By accessing or using the
                Service, you agree to be bound by these Terms. If you do not agree to these Terms,
                you may not access or use the Service.
              </p>
            </section>

            {/* Accepting these Terms */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                1. Accepting these Terms
              </h2>
              <p className="text-gray-700 leading-relaxed">
                By creating an account or using the Service, you acknowledge that you have read,
                understood, and agree to be bound by these Terms and our Privacy Policy. If you
                are using the Service on behalf of an organization, you are agreeing to these
                Terms for that organization and representing that you have the authority to bind
                that organization to these Terms.
              </p>
            </section>

            {/* Changes to these Terms */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                2. Changes to these Terms
              </h2>
              <p className="text-gray-700 leading-relaxed">
                We may modify these Terms at any time. We will notify you of material changes by
                posting the new Terms on our website and updating the "Last Updated" date. Your
                continued use of the Service after such changes constitutes your acceptance of
                the new Terms. If you do not agree to the modified Terms, you must stop using
                the Service.
              </p>
            </section>

            {/* Account Registration */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                3. Account Registration and Security
              </h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                To use certain features of the Service, you must create an account. When creating
                an account, you agree to:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Provide accurate, current, and complete information</li>
                <li>Maintain and update your information to keep it accurate and current</li>
                <li>Maintain the security of your password and account</li>
                <li>Notify us immediately of any unauthorized use of your account</li>
                <li>Accept responsibility for all activities that occur under your account</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                You may not share your account credentials with others or allow others to access
                your account.
              </p>
            </section>

            {/* Use of the Service */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                4. Use of the Service
              </h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                You agree to use the Service only for lawful purposes and in accordance with these
                Terms. You agree not to:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Use the Service in any way that violates any applicable law or regulation</li>
                <li>
                  Use the Service to transmit any harmful, threatening, abusive, or offensive content
                </li>
                <li>Attempt to gain unauthorized access to the Service or its systems</li>
                <li>Use automated systems to access the Service without our permission</li>
                <li>Reverse engineer, decompile, or disassemble any part of the Service</li>
                <li>Use the Service to compete with us or create a similar service</li>
                <li>Resell or redistribute the Service without our written permission</li>
              </ul>
            </section>

            {/* Payment and Billing */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                5. Payment and Billing
              </h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                The Service operates on a pay-per-use model:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Each permit search costs $1.00</li>
                <li>You must maintain a positive account balance to use the search feature</li>
                <li>You can add funds to your account using a credit card processed through Stripe</li>
                <li>All fees are non-refundable except as required by law</li>
                <li>We reserve the right to change our pricing with 30 days' notice</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                By providing payment information, you authorize us to charge your payment method
                for the amounts you select. You are responsible for all charges incurred under
                your account.
              </p>
            </section>

            {/* Search Results and Accuracy */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                6. Search Results and Accuracy
              </h2>
              <p className="text-gray-700 leading-relaxed">
                While we strive to provide accurate permit fee calculations and requirements, the
                Service is for informational purposes only. Permit fees, requirements, and
                regulations may change without notice, and municipalities may have specific rules
                that affect your situation. You should always verify information directly with the
                appropriate municipal authority before relying on it for important decisions. We
                are not responsible for any inaccuracies or errors in the information provided.
              </p>
            </section>

            {/* Intellectual Property */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                7. Intellectual Property Rights
              </h2>
              <p className="text-gray-700 leading-relaxed">
                The Service, including its original content, features, and functionality, is owned
                by euronymo and is protected by international copyright, trademark, patent, trade
                secret, and other intellectual property laws. You may not copy, modify, distribute,
                sell, or lease any part of our Service without our express written permission. You
                may not use our trademarks or service marks without our prior written consent.
              </p>
            </section>

            {/* Your Content */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                8. Your Content and Data
              </h2>
              <p className="text-gray-700 leading-relaxed">
                You retain ownership of any data, information, or content you provide through the
                Service (your "Content"). By using the Service, you grant us a limited license to
                use, store, and process your Content solely to provide and improve the Service.
                We will retain your search history to provide you with access to past searches.
                You can delete your Content at any time by deleting your account.
              </p>
            </section>

            {/* Third-Party Services */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                9. Third-Party Services and Links
              </h2>
              <p className="text-gray-700 leading-relaxed">
                The Service may contain links to third-party websites or services, including
                municipal permit portals. We are not responsible for the content, privacy policies,
                or practices of any third-party websites or services. We do not endorse or make any
                representations about third-party websites or services. Your use of third-party
                websites and services is at your own risk.
              </p>
            </section>

            {/* Disclaimers */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                10. Disclaimers and Limitation of Liability
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND,
                EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF
                MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, TIMELY, SECURE, OR
                ERROR-FREE. WE DO NOT WARRANT THE ACCURACY OR COMPLETENESS OF ANY INFORMATION
                PROVIDED THROUGH THE SERVICE.
              </p>
              <p className="text-gray-700 leading-relaxed">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT SHALL EURONYMO BE LIABLE FOR
                ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS
                OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF
                DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM YOUR USE OF THE
                SERVICE. OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID US IN THE PAST
                SIX MONTHS.
              </p>
            </section>

            {/* Indemnification */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                11. Indemnification
              </h2>
              <p className="text-gray-700 leading-relaxed">
                You agree to indemnify, defend, and hold harmless euronymo, its affiliates, and
                their respective officers, directors, employees, and agents from and against any
                claims, liabilities, damages, losses, and expenses, including reasonable attorneys'
                fees, arising out of or in any way connected with your access to or use of the
                Service, your violation of these Terms, or your violation of any rights of another.
              </p>
            </section>

            {/* Termination */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                12. Termination
              </h2>
              <p className="text-gray-700 leading-relaxed">
                We may terminate or suspend your account and access to the Service immediately,
                without prior notice or liability, for any reason, including if you breach these
                Terms. Upon termination, your right to use the Service will immediately cease. You
                may terminate your account at any time by contacting us. All provisions of these
                Terms that by their nature should survive termination shall survive, including
                ownership provisions, warranty disclaimers, and limitations of liability.
              </p>
            </section>

            {/* Governing Law */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                13. Governing Law and Jurisdiction
              </h2>
              <p className="text-gray-700 leading-relaxed">
                These Terms shall be governed by and construed in accordance with the laws of the
                United States, without regard to its conflict of law provisions. Any disputes
                arising from these Terms or the Service shall be resolved in the courts of
                competent jurisdiction in the United States.
              </p>
            </section>

            {/* Dispute Resolution */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                14. Dispute Resolution
              </h2>
              <p className="text-gray-700 leading-relaxed">
                If you have any concerns or disputes about the Service, you agree to first try to
                resolve the dispute informally by contacting us at support@permitbuddy.com. If we
                are unable to resolve the dispute within 30 days, either party may pursue formal
                dispute resolution.
              </p>
            </section>

            {/* Entire Agreement */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                15. Entire Agreement
              </h2>
              <p className="text-gray-700 leading-relaxed">
                These Terms, together with our Privacy Policy, constitute the entire agreement
                between you and euronymo regarding the Service and supersede all prior agreements
                and understandings, whether written or oral, regarding the subject matter herein.
              </p>
            </section>

            {/* Severability */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                16. Severability
              </h2>
              <p className="text-gray-700 leading-relaxed">
                If any provision of these Terms is found to be unenforceable or invalid, that
                provision will be limited or eliminated to the minimum extent necessary so that
                these Terms will otherwise remain in full force and effect and enforceable.
              </p>
            </section>

            {/* Contact Information */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                17. Contact Information
              </h2>
              <p className="text-gray-700 leading-relaxed">
                If you have any questions about these Terms, please contact us at:
              </p>
              <p className="text-gray-700 leading-relaxed mt-3">
                <strong>Email:</strong>{' '}
                <a href="mailto:support@permitbuddy.com" className="text-primary-600 hover:text-primary-700">
                  support@permitbuddy.com
                </a>
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
